import React from 'react';
import { Settings2, ChevronDown, ChevronUp } from 'lucide-react';

export interface LayoutConfig {
  fontSize: 'small' | 'medium' | 'large';
  pageOrientation: 'portrait' | 'landscape';
  showPageNumbers: boolean;
  showSummaryBox: boolean;
  showGrandTotals: boolean;
  headerStyle: 'minimal' | 'standard' | 'detailed';
  tableStyle: 'compact' | 'comfortable' | 'spacious';
  colorScheme: 'default' | 'grayscale' | 'green' | 'blue';
  paperSize: 'A4' | 'Letter' | 'Legal';
  showLogo?: boolean;
  headerImageUrl?: string;
  headerImagePath?: string;
}

export const DEFAULT_LAYOUT: LayoutConfig = {
  fontSize: 'medium', pageOrientation: 'landscape', showPageNumbers: true,
  showSummaryBox: true, showGrandTotals: true, headerStyle: 'standard',
  tableStyle: 'comfortable', colorScheme: 'default', paperSize: 'A4',
  showLogo: false,
  headerImageUrl: '/assets/report-logo.png',
};

interface Props { value: LayoutConfig; onChange: (c: LayoutConfig) => void; }

const LayoutConfigurator: React.FC<Props> = ({ value, onChange }) => {
  const [open, setOpen] = React.useState(false);
  const set = (patch: Partial<LayoutConfig>) => onChange({ ...value, ...patch });

  const Pill = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) => (
    <button onClick={onClick} className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all
      ${active ? 'bg-primary-600 text-white border-primary-600 shadow-sm' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-primary-400'}`}>
      {children}
    </button>
  );

  const Toggle = ({ checked, onChange: onCh, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) => (
    <label className="flex items-center gap-2 cursor-pointer">
      <div className={`relative w-9 h-5 rounded-full transition-colors ${checked ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'}`}
        onClick={() => onCh(!checked)}>
        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4.5 left-0.5' : 'left-0.5'}`}
          style={{ transform: checked ? 'translateX(16px)' : 'translateX(0)' }} />
      </div>
      <span className="text-xs text-gray-600 dark:text-gray-300">{label}</span>
    </label>
  );

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
        <span className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          <Settings2 className="h-4 w-4 text-primary-500" /> Layout Customization
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
      </button>

      {open && (
        <div className="p-4 space-y-4 bg-white dark:bg-gray-900/50">
          {/* Font Size */}
          <div>
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Font Size</span>
            <div className="flex gap-2 mt-1.5">
              {(['small', 'medium', 'large'] as const).map(s => (
                <Pill key={s} active={value.fontSize === s} onClick={() => set({ fontSize: s })}>{s.charAt(0).toUpperCase() + s.slice(1)}</Pill>
              ))}
            </div>
          </div>

          {/* Orientation */}
          <div>
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Orientation</span>
            <div className="flex gap-2 mt-1.5">
              <Pill active={value.pageOrientation === 'portrait'} onClick={() => set({ pageOrientation: 'portrait' })}>Portrait</Pill>
              <Pill active={value.pageOrientation === 'landscape'} onClick={() => set({ pageOrientation: 'landscape' })}>Landscape</Pill>
            </div>
          </div>

          {/* Color Scheme */}
          <div>
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Color Scheme</span>
            <div className="flex gap-2 mt-1.5">
              {[{ id: 'default' as const, label: 'Classic', color: '#1a5276' }, { id: 'green' as const, label: 'Green', color: '#1e8449' },
                { id: 'blue' as const, label: 'Blue', color: '#1f3a5f' }, { id: 'grayscale' as const, label: 'Gray', color: '#2c3e50' }]
                .map(c => (
                  <button key={c.id} onClick={() => set({ colorScheme: c.id })}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all
                      ${value.colorScheme === c.id ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20 shadow-sm' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-primary-400'}`}>
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: c.color }} />
                    {c.label}
                  </button>
                ))}
            </div>
          </div>

          {/* Table Style */}
          <div>
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Table Density</span>
            <div className="flex gap-2 mt-1.5">
              {(['compact', 'comfortable', 'spacious'] as const).map(s => (
                <Pill key={s} active={value.tableStyle === s} onClick={() => set({ tableStyle: s })}>{s.charAt(0).toUpperCase() + s.slice(1)}</Pill>
              ))}
            </div>
          </div>

          {/* Logo / header image */}
          <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700">
            <Toggle
              checked={value.showLogo === true}
              onChange={v => set({ showLogo: v })}
              label="Show logo on letterhead"
            />
            {value.showLogo && (
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Logo URL (print preview)
                </label>
                <input
                  type="text"
                  className="form-input text-sm w-full mt-1"
                  placeholder="/assets/report-logo.png"
                  value={value.headerImageUrl || ''}
                  onChange={e => set({ headerImageUrl: e.target.value })}
                />
                <p className="text-[10px] text-gray-400 mt-1">
                  Place file in <code className="text-[10px]">public/assets/</code>. PDF export uses server path if configured.
                </p>
              </div>
            )}
          </div>

          {/* Toggles */}
          <div className="flex flex-wrap gap-4 pt-2 border-t border-gray-200 dark:border-gray-700">
            <Toggle checked={value.showSummaryBox} onChange={v => set({ showSummaryBox: v })} label="Summary Box" />
            <Toggle checked={value.showGrandTotals} onChange={v => set({ showGrandTotals: v })} label="Grand Totals" />
            <Toggle checked={value.showPageNumbers} onChange={v => set({ showPageNumbers: v })} label="Page Numbers" />
          </div>
        </div>
      )}
    </div>
  );
};

export default LayoutConfigurator;
